import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useMaintenance } from '../contexts/MaintenanceContext';
import useDeviceType from '../hooks/useDeviceType';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Grid3x3,
  Mail,
  Users,
  Network,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Moon,
  Sun,
  LogOut,
  Wrench,
  Menu,
  X,
  Shield,
  BookOpen,
  RefreshCw,
  KeyRound,
  Settings2,
  Activity,
  CalendarClock,
} from 'lucide-react';
import { toast } from 'sonner';
import MaintenanceModal from './MaintenanceModal';
import { useHelpCenter } from '../contexts/HelpCenterContext';
import { cn } from '../lib/utils';
import { API_URL as API_BASE } from '../apiBase';

const AUTH_MODE = process.env.REACT_APP_AUTH_MODE || "sso";
const CENTRALE_API_URL = process.env.REACT_APP_CENTRALE_API_URL || 'https://app.prysme.eu';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [isUsersExpanded, setIsUsersExpanded] = useState(false);
  const [isSyncExpanded, setIsSyncExpanded] = useState(
    () => location.pathname === '/dashboard' && location.state?.activeTab === 'sync',
  );
  /** Miroir pour le chevron ; menu non contrôlé côté Radix (pas de prop `open`). */
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { maintenanceStatus } = useMaintenance();
  const { isDesktop } = useDeviceType();
  const { openHelp, summary } = useHelpCenter();
  const unviewedCount = summary?.menu_badge_count ?? 0;

  useEffect(() => {
    if (location.pathname === '/dashboard' && location.state?.activeTab === 'sync') {
      setIsSyncExpanded(true);
    }
  }, [location.pathname, location.state?.activeTab]);

  const getPhotoUrl = (photoUrl) => {
    if (!photoUrl) return null;
    // Si c'est du base64, retourner tel quel
    if (photoUrl.startsWith('data:')) return photoUrl;
    // Si c'est une URL externe, retourner tel quel
    if (photoUrl.startsWith('http')) return photoUrl;
    // Sinon, préfixe API (vide = même origine / proxy en dev)
    return `${API_BASE}${photoUrl}`;
  };

  const getInitials = (nom, prenom) => {
    const n = nom?.charAt(0) || '';
    const p = prenom?.charAt(0) || '';
    return `${n}${p}`.toUpperCase();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    {
      label: 'Annuaire',
      icon: Grid3x3,
      path: '/dashboard',
      tab: 'grid',
      show: true
    },
    {
      label: 'Listes',
      icon: Mail,
      path: '/dashboard',
      tab: 'lists',
      show: true
    },
    {
      label: 'Organigramme',
      icon: Network,
      path: '/org-chart',
      show: isDesktop // Visible uniquement sur desktop (Windows, MacOS, Linux)
    }
  ];

  const syncSubItems = [
    {
      label: 'Configuration Compte',
      icon: KeyRound,
      path: '/dashboard',
      tab: 'sync',
      subTab: 'config',
    },
    {
      label: 'Options de synchronisation',
      icon: Settings2,
      path: '/dashboard',
      tab: 'sync',
      subTab: 'options',
    },
    {
      label: 'État des synchronisations',
      icon: Activity,
      path: '/dashboard',
      tab: 'sync',
      subTab: 'status',
    },
    {
      label: 'Programmation',
      icon: CalendarClock,
      path: '/dashboard',
      tab: 'sync',
      subTab: 'schedule',
    },
  ];

  // Sous-menu Utilisateurs (admin seulement)
  const usersSubItems = [
    {
      label: 'Utilisateurs',
      icon: Users,
      path: '/dashboard',
      tab: 'users',
      subTab: 'users'
    },
    {
      label: 'Permissions',
      icon: Shield,
      path: '/dashboard',
      tab: 'users',
      subTab: 'permissions'
    }
  ];

  const isActive = (item) => {
    // For org-chart, simple path match
    if (item.path === '/org-chart' && location.pathname === '/org-chart') {
      return true;
    }
    // For dashboard with tabs, check both path and state
    if (item.path === '/dashboard' && location.pathname === '/dashboard') {
      const currentTab = location.state?.activeTab || 'grid';
      if (item.tab === 'grid' && currentTab === 'grid') return true;
      if (item.tab === 'lists' && currentTab === 'lists') return true;
    }
    return false;
  };

  const isUsersSubItemActive = (subItem) => {
    if (location.pathname === '/dashboard') {
      const currentTab = location.state?.activeTab || 'grid';
      const currentSubTab = location.state?.usersSubTab || 'users';
      if (currentTab === 'users') {
        return subItem.subTab === currentSubTab;
      }
    }
    return false;
  };

  const isUsersMenuActive = () => {
    if (location.pathname === '/dashboard') {
      const currentTab = location.state?.activeTab || 'grid';
      return currentTab === 'users';
    }
    return false;
  };

  const isSyncSubItemActive = (subItem) => {
    if (location.pathname === '/dashboard') {
      const currentTab = location.state?.activeTab || 'grid';
      const rawSubTab = location.state?.syncSubTab || 'config';
      const currentSubTab =
        rawSubTab === 'mail' || rawSubTab === 'entra' ? 'config' : rawSubTab;
      if (currentTab === 'sync') {
        return subItem.subTab === currentSubTab;
      }
    }
    return false;
  };

  const isSyncMenuActive = () => {
    if (location.pathname === '/dashboard') {
      return location.state?.activeTab === 'sync';
    }
    return false;
  };

  const handleNavClick = (item) => {
    if (item.tab) {
      navigate(item.path, { state: { activeTab: item.tab } });
    } else {
      navigate(item.path);
    }
    setIsMobileOpen(false);
  };

  const handleUsersSubItemClick = (subItem) => {
    navigate(subItem.path, { state: { activeTab: subItem.tab, usersSubTab: subItem.subTab } });
    setIsMobileOpen(false);
  };

  const handleUsersMenuClick = () => {
    if (isCollapsed) {
      // En mode réduit, naviguer directement vers Utilisateurs
      navigate('/dashboard', { state: { activeTab: 'users', usersSubTab: 'users' } });
    } else {
      // En mode étendu, toggle le sous-menu
      setIsUsersExpanded(!isUsersExpanded);
    }
  };

  const handleSyncSubItemClick = (subItem) => {
    navigate(subItem.path, { state: { activeTab: subItem.tab, syncSubTab: subItem.subTab } });
    setIsMobileOpen(false);
  };

  const handleSyncMenuClick = () => {
    if (isCollapsed) {
      navigate('/dashboard', { state: { activeTab: 'sync', syncSubTab: 'config' } });
    } else {
      setIsSyncExpanded(!isSyncExpanded);
    }
    setIsMobileOpen(false);
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Zone A - Identité */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_permissionhub-11/artifacts/d9t3issx_AVATAR.png" 
            alt="Logo" 
            className="w-10 h-10 flex-shrink-0"
          />
          {!isCollapsed && (
            <h1 className="text-lg font-bold whitespace-nowrap">Annuaire Interne</h1>
          )}
        </div>
      </div>

      {/* Zone B - Contexte Utilisateur */}
      <div className="p-4 border-b border-border">
        <DropdownMenu modal={false} onOpenChange={setProfileMenuOpen}>
          {/* Pas de onPointerDown sur le trigger : un preventDefault ici peut perturber l’ouverture
              au 1er clic (double-clic requis). La capture est encore aidée par le filtre
              isTrusted sur pointerdown/focus *outside* uniquement. */}
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="profile-menu-trigger"
              aria-label={
                user?.display_name ||
                [user?.prenom, user?.nom].filter(Boolean).join(' ') ||
                'Menu profil'
              }
              className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              {/* Photo utilisateur avec indicateur en ligne */}
              <div className="relative flex-shrink-0">
                {getPhotoUrl(user?.photo_url) ? (
                  <img 
                    src={getPhotoUrl(user?.photo_url)}
                    alt={user?.display_name || `${user?.prenom} ${user?.nom}`}
                    className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 dark:border-gray-600"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#f76f4b] flex items-center justify-center">
                    <span className="text-white font-medium text-sm">
                      {getInitials(user?.nom, user?.prenom)}
                    </span>
                  </div>
                )}
                {/* Pastille verte "en ligne" */}
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full"></div>
              </div>
              
              {/* Nom et position */}
              {!isCollapsed && (
                <div className="flex-1 text-left overflow-hidden min-w-0">
                  <p className="font-semibold text-sm text-[#080808] dark:text-white truncate">
                    {user?.display_name || `${user?.prenom} ${user?.nom}`}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {user?.position?.trim() ? user.position : 'Utilisateur'}
                  </p>
                </div>
              )}
              {!isCollapsed && (
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                    profileMenuOpen ? 'rotate-180' : ''
                  }`}
                  aria-hidden
                />
              )}
            </button>
          </DropdownMenuTrigger>
          
          <DropdownMenuContent
            align="start"
            data-testid="profile-menu-panel"
            className="z-[10000] w-72 p-2 dark:bg-gray-800 dark:border-gray-700"
            sideOffset={5}
            // Calque aligné Centrale (~10k) : au-dessus sidebar (z-40) et proche des overlays capture.
            // Ne bloquer que les interactions non fiables (priorité tutoriel) ; l’utilisateur ferme
            // au clic dehors / sur le trigger / Échap si événements trusted.
            onPointerDownOutside={(e) => {
              const orig = e.detail?.originalEvent;
              if (orig && orig.isTrusted === false) {
                e.preventDefault();
              }
            }}
            onFocusOutside={(e) => {
              const orig = e.detail?.originalEvent;
              if (orig && orig.isTrusted === false) {
                e.preventDefault();
              }
            }}
          >
            {/* 1. TOGGLE MODE SOMBRE/CLAIR */}
            <div 
              className="mb-2 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleTheme();
              }}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 flex-shrink-0">
                  {theme === 'light' ? (
                    <Sun size={18} className="text-yellow-500" />
                  ) : (
                    <Moon size={18} className="text-blue-400" />
                  )}
                  <span className="text-sm font-medium dark:text-white whitespace-nowrap">
                    {theme === 'light' ? 'Mode clair' : 'Mode sombre'}
                  </span>
                </div>
                {/* Toggle Switch */}
                <div
                  className={`relative w-14 h-7 rounded-full transition-colors duration-300 flex-shrink-0 ${
                    theme === 'dark' ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-300 ${
                      theme === 'dark' ? 'translate-x-8' : 'translate-x-1'
                    }`}
                  />
                </div>
              </div>
            </div>
            
            <DropdownMenuSeparator className="dark:bg-gray-700 my-2" />

            {/* 2. BOUTONS EXISTANTS (Mode Maintenance si admin) */}
            {user?.is_admin && (
              <>
                <DropdownMenuItem
                  onClick={() => {
                    if (maintenanceStatus.is_active) {
                      toast.info('Utilisez le bandeau pour désactiver la maintenance');
                    } else {
                      setShowMaintenanceModal(true);
                    }
                  }}
                  className={`cursor-pointer dark:text-gray-200 dark:hover:bg-gray-700 rounded-lg ${
                    maintenanceStatus.is_active ? 'text-amber-500' : ''
                  }`}
                >
                  <Wrench size={16} className="mr-2" />
                  Mode Maintenance
                </DropdownMenuItem>
                <DropdownMenuSeparator className="dark:bg-gray-700 my-2" />
              </>
            )}
            
            {/* 3. DÉCONNEXION / RETOUR PRYSMA (TOUJOURS EN DERNIER) */}
            <DropdownMenuItem 
              onClick={() => {
                if (AUTH_MODE === "sso") {
                  // Mode SSO : Retour vers Prysma
                  logout();
                  window.location.href = CENTRALE_API_URL;
                } else {
                  // Mode Local : Déconnexion classique
                  handleLogout();
                }
              }}
              className={`cursor-pointer dark:hover:bg-gray-700 rounded-lg ${
                AUTH_MODE === "sso" 
                  ? "text-cyan-600 dark:text-cyan-400" 
                  : "text-red-600 dark:text-red-400"
              }`}
            >
              <LogOut size={16} className="mr-2" />
              {AUTH_MODE === "sso" ? "Retour Prysma" : "Déconnexion"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Zone C - Navigation Principale */}
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        {navItems.map((item) => {
          if (!item.show) return null;

          const Icon = item.icon;
          const active = isActive(item);

          return (
            <button
              key={item.label}
              onClick={() => handleNavClick(item)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                active
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-accent'
              }`}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              {!isCollapsed && (
                <span className="font-medium">{item.label}</span>
              )}
            </button>
          );
        })}

        {user?.is_admin && (
          <div className="space-y-2 border-t border-gray-200 pt-4 dark:border-gray-700">
            {!isCollapsed && (
              <p className="mb-2 px-4 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Administration
              </p>
            )}
            <div>
              <button
                onClick={handleUsersMenuClick}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isUsersMenuActive()
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-accent'
                }`}
              >
                <Users className="h-5 w-5 flex-shrink-0" />
                {!isCollapsed && (
                  <>
                    <span className="font-medium flex-1 text-left">Utilisateurs</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${isUsersExpanded ? 'rotate-180' : ''}`} />
                  </>
                )}
              </button>

              {!isCollapsed && isUsersExpanded && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-border pl-3">
                  {usersSubItems.map((subItem) => {
                    const SubIcon = subItem.icon;
                    const subActive = isUsersSubItemActive(subItem);

                    return (
                      <button
                        key={subItem.label}
                        onClick={() => handleUsersSubItemClick(subItem)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm ${
                          subActive
                            ? 'bg-primary/80 text-primary-foreground'
                            : 'hover:bg-accent'
                        }`}
                      >
                        <SubIcon className="h-4 w-4 flex-shrink-0" />
                        <span>{subItem.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <button
                type="button"
                onClick={handleSyncMenuClick}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isSyncMenuActive()
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-accent'
                }`}
              >
                <RefreshCw className="h-5 w-5 flex-shrink-0" />
                {!isCollapsed && (
                  <>
                    <span className="font-medium flex-1 text-left">Synchronisation</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${isSyncExpanded ? 'rotate-180' : ''}`} />
                  </>
                )}
              </button>

              {!isCollapsed && isSyncExpanded && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-border pl-3">
                  {syncSubItems.map((subItem) => {
                    const SubIcon = subItem.icon;
                    const subActive = isSyncSubItemActive(subItem);

                    return (
                      <button
                        key={subItem.label}
                        type="button"
                        onClick={() => handleSyncSubItemClick(subItem)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm ${
                          subActive
                            ? 'bg-primary/80 text-primary-foreground'
                            : 'hover:bg-accent'
                        }`}
                      >
                        <SubIcon className="h-4 w-4 flex-shrink-0" />
                        <span className="text-left">{subItem.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Zone D - Pied : centre d&apos;aide (desktop) + réduction */}
      <div className="border-t border-gray-200 dark:border-gray-700">
        <div className={cn('flex flex-col gap-2 p-3', isCollapsed && 'px-2')}>
          {isDesktop && (
            <button
              type="button"
              onClick={() => openHelp()}
              title={
                unviewedCount > 0
                  ? `Centre d'aide — ${unviewedCount} à voir`
                  : isCollapsed
                    ? "Centre d'aide"
                    : undefined
              }
              aria-label={
                unviewedCount > 0
                  ? `Centre d'aide, ${unviewedCount} élément${unviewedCount > 1 ? 's' : ''} à voir`
                  : isCollapsed
                    ? "Centre d'aide"
                    : undefined
              }
              className={cn(
                'flex w-full items-center gap-2 rounded-lg text-sm text-gray-700 transition-colors dark:text-gray-200',
                'hover:bg-gray-100 dark:hover:bg-gray-700',
                isCollapsed ? 'justify-center p-2' : 'relative justify-center px-3 py-2.5',
              )}
            >
              <span className="relative inline-flex shrink-0">
                <BookOpen
                  size={20}
                  className={cn(
                    'text-[#f76f4b]',
                    unviewedCount > 0 && 'motion-safe:animate-help-book-attention',
                  )}
                  aria-hidden
                />
                {isCollapsed && unviewedCount > 0 && (
                  <span
                    className="absolute -right-1 -top-0.5 h-2 min-w-[8px] rounded-full bg-purple-500 ring-2 ring-white dark:ring-gray-800"
                    aria-hidden
                  />
                )}
              </span>
              {!isCollapsed && (
                <span className="shrink-0 whitespace-nowrap">Centre d&apos;aide</span>
              )}
            </button>
          )}
          <button
            type="button"
            onClick={() => setIsCollapsed(!isCollapsed)}
            aria-label={isCollapsed ? 'Agrandir la barre latérale' : 'Réduire la barre latérale'}
            className={cn(
              'flex w-full items-center justify-center rounded-lg p-2 transition-colors hover:bg-accent',
              isDesktop && 'mt-2 border-t border-gray-200 pt-2 dark:border-gray-700',
            )}
          >
            {isCollapsed ? (
              <ChevronRight className="h-5 w-5" />
            ) : (
              <ChevronLeft className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-card border border-border"
      >
        {isMobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:sticky top-0 left-0 h-screen bg-card border-r border-border
          transition-all duration-300 z-40
          ${isCollapsed ? 'w-20' : 'w-64'}
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <SidebarContent />
      </aside>

      {/* Maintenance Modal */}
      <MaintenanceModal
        isOpen={showMaintenanceModal}
        onClose={() => setShowMaintenanceModal(false)}
      />
    </>
  );
};

export default Sidebar;
